/**
 * Reusable Area fields for Unit form.
 * "+ Add area row" opens a menu → user picks preset or Custom → row renders inline.
 */
import React, { useState, useRef } from 'react';
import {
  Box, TextField, Button, IconButton,
  FormControl, InputLabel, Select, MenuItem,
  Menu, ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import TuneIcon from '@mui/icons-material/Tune';
import { SectionHeader, SECTION_COLORS, AREA_UNIT_OPTIONS } from '../../utils/inventory_helper';

const PRESET_AREA_OPTIONS = [
  { value: 'total_area',        label: 'Total Area' },
  { value: 'carpet_area',       label: 'Carpet Area' },
  { value: 'super_builtup_area',label: 'Super Built-up Area' },
  { value: 'builtup_area',      label: 'Built-up Area' },
];

const ROW_INPUT_HEIGHT = 40;

const fieldSx = (accentColor = SECTION_COLORS.teal) => ({
  '& .MuiInputBase-root': { height: ROW_INPUT_HEIGHT, minHeight: ROW_INPUT_HEIGHT, fontSize: 12, borderRadius: 1.5 },
  '& .MuiInputLabel-root': { fontSize: 12, color: SECTION_COLORS.sub, '&.Mui-focused': { color: accentColor } },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accentColor, borderWidth: 1.5 },
  },
  '& .MuiInputBase-input::placeholder': { fontSize: 12, opacity: 0.6 },
});

const slugFromLabel = (label) =>
  (label || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || '';

export function getAreaRowSlug(row) {
  if (row.key && PRESET_AREA_OPTIONS.some((o) => o.value === row.key)) return row.key;
  return slugFromLabel(row.label) || null;
}

const InventoryAreaFields = ({
  value = [],
  onChange,
  areaUnit = 'sqft',
  areaUnitOptions = AREA_UNIT_OPTIONS,
  accent = 'info',
  sx = {},
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const addBtnRef = useRef(null);

  const unitOpts = areaUnitOptions?.length ? areaUnitOptions : AREA_UNIT_OPTIONS;
  const list = Array.isArray(value) ? value : [];

  // Keys already used in existing rows
  const usedKeys = list.map((r) => getAreaRowSlug(r)).filter(Boolean);
  const isKeyUsed = (key, selfIndex = -1) =>
    list.some((r, i) => i !== selfIndex && getAreaRowSlug(r) === key);

  /** Open the add-menu */
  const handleAddClick = (e) => setMenuAnchor(e.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  /** User picked a preset from the menu */
  const handlePickPreset = (preset) => {
    handleMenuClose();
    onChange([...list, { key: preset.value, label: preset.label, value: '', unit: areaUnit || 'sqft' }]);
  };

  /** User picked "Custom" from the menu */
  const handlePickCustom = () => {
    handleMenuClose();
    onChange([...list, { key: '', label: '', value: '', unit: areaUnit || 'sqft' }]);
  };

  const updateAt = (index, field, val) => {
    const next = list.map((row, i) => {
      if (i !== index) return row;
      const updated = { ...row, [field]: val };
      // For custom rows: auto-derive key from label
      if (field === 'label') updated.key = slugFromLabel(val);
      return updated;
    });
    onChange(next);
  };

  const removeAt = (index) => {
    onChange(list.filter((_, i) => i !== index));
  };

  const isCustomType = (row) => !row.key || !PRESET_AREA_OPTIONS.some((o) => o.value === row.key);

  return (
    <Box sx={{ ...sx }}>
      <SectionHeader
        title="Area"
        subtitle="Add area types (e.g. carpet, built-up) with value and unit"
        accent={accent}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {list.map((row, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              gap: 1.25,
              alignItems: 'flex-start',
              flexWrap: 'nowrap',
              minHeight: ROW_INPUT_HEIGHT,
            }}
          >
            {isCustomType(row) ? (
              /* Custom row: text input for label */
              <TextField
                placeholder="Custom label"
                value={row.label ?? ''}
                onChange={(e) => updateAt(index, 'label', e.target.value)}
                size="small"
                label="Label"
                InputLabelProps={{ shrink: true }}
                sx={{ ...fieldSx(), minWidth: 150, flexShrink: 0 }}
                variant="outlined"
                error={isKeyUsed(getAreaRowSlug(row), index)}
                helperText={isKeyUsed(getAreaRowSlug(row), index) ? 'Duplicate label' : ''}
              />
            ) : (
              /* Preset row: static chip/label showing type name */
              <Box
                sx={{
                  height: ROW_INPUT_HEIGHT,
                  minWidth: 150,
                  display: 'flex',
                  alignItems: 'center',
                  px: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'text.primary',
                  bgcolor: 'grey.50',
                  flexShrink: 0,
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {row.label}
              </Box>
            )}

            {/* Value */}
            <TextField
              placeholder="Value"
              value={row.value ?? ''}
              onChange={(e) => updateAt(index, 'value', e.target.value)}
              type="number"
              inputProps={{ min: 0, step: 50 }}
              size="small"
              sx={{ ...fieldSx(), width: 90, flexShrink: 0 }}
              variant="outlined"
            />

            {/* Unit */}
            <FormControl size="small" sx={{ ...fieldSx(), minWidth: 90, flexShrink: 0 }}>
              <InputLabel id={`area-unit-${index}`} shrink>Unit</InputLabel>
              <Select
                labelId={`area-unit-${index}`}
                label="Unit"
                value={row.unit || areaUnit || 'sqft'}
                onChange={(e) => updateAt(index, 'unit', e.target.value)}
              >
                {unitOpts.map((u) => (
                  <MenuItem key={u.value} value={u.value} sx={{ fontSize: 12.5 }}>
                    {u.label ?? u.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Delete */}
            <IconButton
              size="small"
              onClick={() => removeAt(index)}
              sx={{
                mt: '2px',
                color: SECTION_COLORS.sub,
                width: ROW_INPUT_HEIGHT,
                height: ROW_INPUT_HEIGHT,
                flexShrink: 0,
              }}
              aria-label="Remove row"
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        ))}

        {/* Add button */}
        <Button
          ref={addBtnRef}
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={handleAddClick}
          size="small"
          sx={{
            textTransform: 'none',
            fontSize: 12,
            color: SECTION_COLORS.teal,
            alignSelf: 'flex-start',
          }}
        >
          Add area row
        </Button>

        {/* Picker menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 3,
            sx: { minWidth: 200, borderRadius: 2, mt: 0.5 },
          }}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        >
          {PRESET_AREA_OPTIONS.map((o) => {
            const used = usedKeys.includes(o.value);
            return (
              <MenuItem
                key={o.value}
                onClick={() => !used && handlePickPreset(o)}
                disabled={used}
                sx={{ fontSize: 12.5, py: 0.75 }}
              >
                <ListItemIcon sx={{ minWidth: 30 }}>
                  <SquareFootIcon sx={{ fontSize: 16, color: used ? 'text.disabled' : SECTION_COLORS.teal }} />
                </ListItemIcon>
                <ListItemText
                  primary={o.label}
                  primaryTypographyProps={{ fontSize: 12.5 }}
                  secondary={used ? 'Already added' : null}
                  secondaryTypographyProps={{ fontSize: 11 }}
                />
              </MenuItem>
            );
          })}
          <Divider sx={{ my: 0.5 }} />
          <MenuItem onClick={handlePickCustom} sx={{ fontSize: 12.5, py: 0.75 }}>
            <ListItemIcon sx={{ minWidth: 30 }}>
              <TuneIcon sx={{ fontSize: 16, color: SECTION_COLORS.teal }} />
            </ListItemIcon>
            <ListItemText
              primary="Custom"
              secondary="Enter your own label"
              primaryTypographyProps={{ fontSize: 12.5 }}
              secondaryTypographyProps={{ fontSize: 11 }}
            />
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default InventoryAreaFields;
export { PRESET_AREA_OPTIONS };